import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../../../utils';
import { Edit, BookOpen, Eye } from 'lucide-react';

const statusColors = {
  planned: 'bg-blue-100 text-blue-700',
  ongoing: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const statusLabels = {
  planned: 'Direncanakan',
  ongoing: 'Berlangsung',
  completed: 'Selesai',
  cancelled: 'Dibatalkan',
};

const formatCurrency = value => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = dateString => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // If invalid, try to parse as date string (YYYY-MM-DD)
      const parts = dateString.split('T')[0].split('-');
      if (parts.length === 3) {
        return new Intl.DateTimeFormat('id-ID', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(new Date(parts[0], parts[1] - 1, parts[2]));
      }
      return dateString;
    }

    return new Intl.DateTimeFormat('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    // Fallback: if date string is in YYYY-MM-DD format, format it manually
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
      const parts = dateString.split('T')[0].split('-');
      const months = [
        'Januari',
        'Februari',
        'Maret',
        'April',
        'Mei',
        'Juni',
        'Juli',
        'Agustus',
        'September',
        'Oktober',
        'November',
        'Desember',
      ];
      return `${parseInt(parts[2])} ${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
    }
    return dateString;
  }
};

function LoadingSkeleton() {
  return (
    <div className="flex justify-center py-8">
      <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
    </div>
  );
}

function EmptyState() {
  return (
    <tr>
      <td colSpan={6} className="text-center py-8 text-gray-500">
        <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Tidak ada data program</p>
      </td>
    </tr>
  );
}

function DevelopmentRow({ item }) {
  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-4 py-3 font-medium">{item.year}</td>
      <td className="px-4 py-3">{item.program_name}</td>
      <td className="px-4 py-3 hidden md:table-cell">{formatDate(item.target_date)}</td>
      <td className="px-4 py-3 hidden lg:table-cell">{formatCurrency(item.budget)}</td>
      <td className="px-4 py-3">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[item.status] || 'bg-gray-100 text-gray-700'}`}
        >
          {statusLabels[item.status] || item.status}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <Link to={createPageUrl(`EmployeeDevelopmentDetail?id=${item.id}`)}>
            <button className="p-2 hover:bg-gray-100 rounded-lg" title="Lihat Detail">
              <Eye className="w-4 h-4" />
            </button>
          </Link>
          <Link to={createPageUrl(`EmployeeDevelopmentForm?id=${item.id}`)}>
            <button className="p-2 hover:bg-gray-100 rounded-lg" title="Edit">
              <Edit className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </td>
    </tr>
  );
}

export default function DevelopmentTable({ programs, loading }) {
  return (
    <div className="overflow-x-auto">
      {loading ? (
        <LoadingSkeleton />
      ) : (
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Tahun</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">
                Nama Program
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden md:table-cell">
                Target
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden lg:table-cell">
                Budget
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {programs.length === 0 ? (
              <EmptyState />
            ) : (
              programs.map(item => <DevelopmentRow key={item.id} item={item} />)
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
